import { useEffect, useRef, useState } from 'react'
import { api } from '../../lib/api.js'
import { toast } from '../../components/feedback/Toast.jsx'

/**
 * The password interaction, exactly as specced (Charles, 2026-07-31):
 *
 *     tap 1 (masked)   →  revealed AND on the clipboard
 *     tap 2 (revealed) →  hidden again
 *
 * Copying happens on reveal, so the second tap is free to close. Same
 * behaviour for every secret, phone and desktop. What keeps it safe without
 * adding friction:
 *   * value is fetched only on tap (one audited reveal per use)
 *   * re-masks and forgets the value after 20s if not closed by hand
 *   * clipboard best-effort clears at 60s — only where the browser allows a
 *     SILENT check (never a permission prompt; annoyance defeats the point)
 *
 * Safari note: clipboard writes must stay inside the user gesture, so the
 * fetch is threaded through ClipboardItem-with-promise where supported, with
 * plain writeText as the fallback. Worst case (old browser, slow network) the
 * value reveals with a "select to copy" hint — never worse than that.
 */

const REMASK_MS = 20_000
const CLIPBOARD_CLEAR_MS = 60_000

export default function TapSecret({ credentialId, className = '' }) {
  const [value, setValue] = useState(null) // null = masked
  const [busy, setBusy] = useState(false)
  const timers = useRef([])
  const btnRef = useRef(null)

  useEffect(() => () => timers.current.forEach(clearTimeout), [])

  const later = (fn, ms) => timers.current.push(setTimeout(fn, ms))

  function hide() {
    setValue(null)
    // iOS keeps the select-all highlight painted after the value re-masks;
    // clear it — but only if the selection is actually ours, so a text
    // selection elsewhere on the page survives the 20 s auto re-mask.
    const sel = window.getSelection()
    if (sel?.anchorNode && btnRef.current?.contains(sel.anchorNode)) sel.removeAllRanges()
  }

  async function copyViaPromise(secretPromise) {
    // Keeps the clipboard write inside the original tap gesture.
    const item = new ClipboardItem({
      'text/plain': secretPromise.then((s) => new Blob([s], { type: 'text/plain' })),
    })
    await navigator.clipboard.write([item])
  }

  function scheduleCleanup(secret) {
    later(hide, REMASK_MS)
    later(async () => {
      try {
        const perm = await navigator.permissions.query({ name: 'clipboard-read' })
        if (perm.state !== 'granted') return // never prompt
        const current = await navigator.clipboard.readText()
        if (current === secret) await navigator.clipboard.writeText('')
      } catch {
        /* browser doesn't allow a silent check — leave the clipboard alone */
      }
    }, CLIPBOARD_CLEAR_MS)
  }

  async function tap() {
    if (busy) return

    // Second tap while revealed: hide it. The copy already happened on
    // reveal, so closing is the only job left.
    if (value) {
      hide()
      return
    }

    setBusy(true)
    const secretPromise = api(`/credentials/${credentialId}/reveal`, { method: 'POST' }).then((d) => d.secret)
    try {
      let copied = false
      if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
        try {
          await copyViaPromise(secretPromise)
          copied = true
        } catch {
          /* fall through to writeText */
        }
      }
      const secret = await secretPromise
      if (!copied) {
        try {
          await navigator.clipboard.writeText(secret)
          copied = true
        } catch {
          /* reveal still works; second tap copies */
        }
      }
      setValue(secret)
      scheduleCleanup(secret)
      toast(copied ? 'Copied' : 'Revealed — tap again to copy')
    } catch (err) {
      toast(err.status === 403 ? 'Your account can’t view passwords' : 'Couldn’t load password', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <button
      ref={btnRef}
      type="button"
      onClick={tap}
      title={value ? 'Tap to copy' : 'Tap to reveal and copy'}
      className={
        'tap inline-flex max-w-full items-center rounded-lg border bg-white px-3 py-1.5 font-mono text-[13px] ' +
        // revealed = armed: the border goes tally red until it re-masks
        (value ? 'select-all border-danger text-ink' : 'border-hairline tracking-widest text-muted') +
        (busy ? ' opacity-50' : '') +
        ' ' +
        className
      }
    >
      <span className="truncate">{value ?? '••••••••'}</span>
    </button>
  )
}
